import React, { useState } from 'react';
import { FAQ } from '../types';
import { useAdminLang } from '../App';
import { ADMIN_LABELS } from '../i18n';
import './FAQEditor.scss';

interface Props {
  faq?: FAQ;
  onSave: (faq: Partial<FAQ>) => Promise<void>;
  onCancel: () => void;
}

const cfg = (window as any)._adminEnv_ || {};
const BACKEND = (cfg.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

function getStr(val: any, lang: string): string {
  if (!val) return '';
  if (typeof val === 'string') return lang === 'en' ? val : '';
  return val[lang] || '';
}

const CATEGORY_COLORS: Record<string, string> = {
  emergency: '#c62828',
  medical: '#e65100',
  fire: '#bf360c',
  information: '#1565c0',
  safety: '#2e7d32',
};

export default function FAQEditor({ faq, onSave, onCancel }: Props) {
  const { lang } = useAdminLang();
  const t = ADMIN_LABELS[lang];

  // Primary language = admin's selected language
  const [primaryQ, setPrimaryQ] = useState(getStr(faq?.question, lang));
  const [primaryA, setPrimaryA] = useState(getStr(faq?.answer, lang));

  // Secondary translation fields (only visible in edit mode expand)
  const otherLangs = (['en', 'ar', 'ur'] as const).filter(l => l !== lang);
  const [transQ, setTransQ] = useState<Record<string, string>>(
    Object.fromEntries(otherLangs.map(l => [l, getStr(faq?.question, l)]))
  );
  const [transA, setTransA] = useState<Record<string, string>>(
    Object.fromEntries(otherLangs.map(l => [l, getStr(faq?.answer, l)]))
  );

  const [showTrans, setShowTrans] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Track whether translations were pre-filled via auto-translate so the save
  // can send the full object instead of triggering another LLM call on the backend.
  const [translated, setTranslated] = useState(false);

  const langLabels: Record<string, string> = {
    en: t.langEn,
    ar: t.langAr,
    ur: t.langUr,
  };
  const isRtl = (l: string) => l === 'ar' || l === 'ur';

  const handleAutoTranslate = async () => {
    if (!primaryQ.trim() || !primaryA.trim()) return;
    setTranslating(true);
    setError('');
    try {
      // Single LLM call: translates question + answer to all 3 languages at once
      const res = await fetch(`${BACKEND}/api/admin/translate-faq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: primaryQ, answer: primaryA }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();

      const newTransQ: Record<string, string> = {};
      const newTransA: Record<string, string> = {};
      otherLangs.forEach(l => {
        newTransQ[l] = data.question?.[l] || transQ[l];
        newTransA[l] = data.answer?.[l]   || transA[l];
      });
      setTransQ(newTransQ);
      setTransA(newTransA);
      setTranslated(true);
      setShowTrans(true);
    } catch {
      setError(t.editorSaveFail);
    } finally {
      setTranslating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!primaryQ.trim() || !primaryA.trim()) {
      setError(t.editorRequired);
      return;
    }
    setError('');
    setSaving(true);

    try {
      if (faq) {
        // Edit mode: PUT with full multilingual object
        const question = { en: '', ar: '', ur: '', [lang]: primaryQ.trim() } as { en: string; ar: string; ur: string };
        const answer  = { en: '', ar: '', ur: '', [lang]: primaryA.trim() } as { en: string; ar: string; ur: string };
        otherLangs.forEach(l => {
          (question as any)[l] = transQ[l] || '';
          (answer as any)[l]   = transA[l] || '';
        });
        await onSave({ question, answer });
      } else if (translated) {
        // Add mode — user already clicked Auto-Translate: send the full multilingual
        // object so the backend skips the LLM translation call entirely.
        const question = { [lang]: primaryQ.trim() } as { en: string; ar: string; ur: string };
        const answer   = { [lang]: primaryA.trim() } as { en: string; ar: string; ur: string };
        otherLangs.forEach(l => {
          (question as any)[l] = transQ[l] || '';
          (answer as any)[l]   = transA[l] || '';
        });
        await onSave({ question, answer });
      } else {
        // Add mode — no preview yet: send raw text, backend auto-translates
        await onSave({
          question: primaryQ.trim(),
          answer: primaryA.trim(),
        } as any);
      }
    } catch {
      setError(t.editorSaveFail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={`faq-editor${lang === 'ar' ? ' rtl' : ''}`} onSubmit={handleSubmit} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <h3 className="editor-title">{faq ? t.editorEditTitle : t.editorAddTitle}</h3>

      {/* Category badge (edit only, read-only) */}
      {faq?.intent && (
        <div className="editor-category-row">
          <span className="editor-category-label">{t.categoryLabel}:</span>
          <span
            className="editor-category-badge"
            style={{ background: CATEGORY_COLORS[faq.intent] || '#555' }}
          >
            {faq.intent}
          </span>
        </div>
      )}

      {/* Primary language fields */}
      <div className="editor-section">
        <h4>{t.questionLabel} ({langLabels[lang]})</h4>
        <textarea
          value={primaryQ}
          onChange={e => setPrimaryQ(e.target.value)}
          rows={2}
          placeholder={t.questionPlaceholder}
          dir={isRtl(lang) ? 'rtl' : 'ltr'}
          required
        />
      </div>

      <div className="editor-section">
        <h4>{t.answerLabel} ({langLabels[lang]})</h4>
        <textarea
          value={primaryA}
          onChange={e => setPrimaryA(e.target.value)}
          rows={4}
          placeholder={t.answerPlaceholder}
          dir={isRtl(lang) ? 'rtl' : 'ltr'}
          required
        />
      </div>

      {/* Auto-translate button */}
      <button
        type="button"
        className="btn-translate"
        onClick={handleAutoTranslate}
        disabled={translating || (!primaryQ.trim() && !primaryA.trim())}
      >
        {translating ? t.translating : t.translateBtn}
      </button>

      {/* Secondary translation fields (expandable) — shown in edit mode always,
          and in add mode once auto-translate has been run */}
      {(faq || translated) && (
        <div className="editor-translations">
          <button
            type="button"
            className="trans-toggle"
            onClick={() => setShowTrans(o => !o)}
          >
            {showTrans ? t.hideTranslations : t.viewTranslations}
          </button>

          {showTrans && otherLangs.map(l => (
            <div key={l} className="editor-trans-group">
              <div className="editor-section">
                <h4>{t.questionLabel} ({langLabels[l]})</h4>
                <textarea
                  value={transQ[l] || ''}
                  onChange={e => setTransQ(q => ({ ...q, [l]: e.target.value }))}
                  rows={2}
                  dir={isRtl(l) ? 'rtl' : 'ltr'}
                  placeholder={`${t.questionPlaceholder} (${langLabels[l]})`}
                />
              </div>
              <div className="editor-section">
                <h4>{t.answerLabel} ({langLabels[l]})</h4>
                <textarea
                  value={transA[l] || ''}
                  onChange={e => setTransA(a => ({ ...a, [l]: e.target.value }))}
                  rows={3}
                  dir={isRtl(l) ? 'rtl' : 'ltr'}
                  placeholder={`${t.answerPlaceholder} (${langLabels[l]})`}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <div className="editor-error">{error}</div>}

      <div className="editor-btns">
        <button type="button" className="btn-secondary" onClick={onCancel}>{t.cancel}</button>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? t.saving : faq ? t.saveBtn : t.addBtn}
        </button>
      </div>
    </form>
  );
}
